import { memo, useContext } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import styled, { type CSSObject } from '@emotion/styled';

import type { ThemeProps } from '@emotion/react';

import bp from '../../../../lib/cssMediaQuery';
import { themeFont } from '../../../../lib/themeFont';
import { useNavLink } from '../../../../lib/useNavLinks';
import { WWContext, type WWContextValue } from '../../../WWContext';
import NavListItem from './Item';

import {
  isWwsWeddingWebsiteV2,
  type Wws_Page,
} from '../../../../types/graphql';

const defaultColors = {
  background: '#ffffff',
  nav: '#414042',
} as const;

const DesktopNav = styled("nav")`
  display: flex;
  width: 100%;
  align-items: center;
  padding-left: 2.5rem;
  padding-right: 1.5rem;
  padding-bottom: 1rem;
  padding-top: 1rem;
  min-height: 8rem;
  background: ${defaultColors.background};

  ${bp['max768']} {
    position: fixed;
    min-height: auto;
    height: 60px;
    top: 0;
    left: 60px;
    right: 60px;
    width: auto;
    text-align: center;
    background: transparent;
    z-index: 20;
    padding: 0;
  }

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.Nav?.Background}
`;

const NavList = styled("ul")`
  li {
    list-style: none;
    margin-right: 1.5rem;
  }

  padding-left: 1rem;
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  justify-content: flex-end;

  ${bp['max768']} {
    display: none;
  }
`;

const Names = styled("span")`
  font-size: 0.875rem;
  display: inline-block;
  text-transform: uppercase;
  color: ${defaultColors.nav};

  ${themeFont('s6')}

  ${bp['max768']} {
    margin: 0 auto;
    font-size: 1.25rem;
  }

  ${({ theme }: ThemeProps): CSSObject | undefined => theme.liteSite?.Nav?.Link}

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.Nav?.Names}
`;

function DesktopLiteNav() {
  const { data } = useContext<WWContextValue>(WWContext);

  const pages = useNavLink();

  const { query } = useRouter();

  const { pathPrefix, slug } = query;

  const href = `/${pathPrefix}/${slug}/`;

  if (!isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
    return null;
  }

  const hasCoupleNames =
    Boolean(data?.weddingWebsiteResult.firstName) &&
    Boolean(data?.weddingWebsiteResult.fianceFirstName);

  return (
    <DesktopNav>
      {hasCoupleNames && (
        <Link href={href} as={href}>
          <Names>
            {data?.weddingWebsiteResult.firstName?.substring(0, 1) || ''}{' '}
            <span>&</span>{' '}
            {data?.weddingWebsiteResult.fianceFirstName?.substring(0, 1) || ''}
          </Names>
        </Link>
      )}

      <NavList>
        {pages?.map((page: Partial<Wws_Page>) => {
          return <NavListItem key={page.id} page={page} query={query} />;
        })}
      </NavList>
    </DesktopNav>
  );
}

export default memo(DesktopLiteNav);
